/**
 * ConsultantProfileSection — iter638
 *
 * Споделен компонент за пълния профил на консултант. Използва се на
 * /consultants/inkov и /consultants/ivanova. Listing-страницата /consultants
 * показва само резюмета и линкове към пълните профили.
 *
 * Props:
 *   consultant: {
 *     photo, name, role, location, accent: "fuchsia" | "cyan",
 *     orcid?, orcid_url?, orcid_label?,
 *     bio: [paragraph, ...],     // първи параграф = резюме
 *     edu_title, edu: [..],
 *     quals_title, quals: [..],
 *     role_in_app_title, role_in_app,
 *   }
 */
import React from "react";
import { useTheme } from "@/theme/ThemeContext";
import {
  GraduationCap, Award, Globe2, ExternalLink, Heart, Smile, BookOpen, CalendarCheck,
} from "lucide-react";

function ChipList({ items, isDark, color }) {
  const palette = {
    fuchsia: isDark
      ? "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30"
      : "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
    cyan: isDark
      ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/30"
      : "bg-cyan-50 text-cyan-800 border-cyan-200",
  }[color];
  return (
    <ul className="space-y-2">
      {(items || []).map((it, i) => (
        <li
          key={i}
          className={`flex items-start gap-2 text-sm leading-relaxed px-3 py-2 rounded-lg border ${palette}`}
        >
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ConsultantProfileSection({ consultant, testIdPrefix }) {
  const { isDark } = useTheme();
  const c = consultant;
  const accent = c.accent || "fuchsia";
  const isFuchsia = accent === "fuchsia";

  const cardBorder = isDark
    ? (isFuchsia ? "border-fuchsia-500/30" : "border-cyan-500/30")
    : (isFuchsia ? "border-fuchsia-200" : "border-cyan-200");
  const cardBg = isDark
    ? "bg-[hsl(223,47%,9%)]"
    : (isFuchsia ? "bg-gradient-to-br from-fuchsia-50 to-white" : "bg-white");
  const ringColor = isFuchsia ? "ring-fuchsia-300/40" : "ring-cyan-300/40";
  const roleColor = isDark
    ? (isFuchsia ? "text-fuchsia-300" : "text-cyan-300")
    : (isFuchsia ? "text-fuchsia-700" : "text-cyan-700");
  const roleInAppBg = isDark
    ? (isFuchsia ? "bg-fuchsia-500/10 text-fuchsia-200" : "bg-cyan-500/10 text-cyan-200")
    : (isFuchsia ? "bg-fuchsia-50 text-fuchsia-900" : "bg-cyan-50 text-cyan-900");
  const iconColor = isFuchsia
    ? { color: "var(--brand-pink)" }
    : { color: "rgb(8 145 178)" };
  const RoleIcon = isFuchsia ? Heart : Smile;

  return (
    <section
      className="py-12 sm:py-16"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        {/* LEFT: photo card */}
        <aside className="lg:col-span-4">
          <div className={`p-6 rounded-2xl border ${cardBorder} ${cardBg} ${isFuchsia ? "" : "shadow-sm"}`}>
            <div className={`w-full aspect-square rounded-2xl overflow-hidden mb-4 ring-2 ${ringColor}`}>
              <img
                src={c.photo}
                alt={c.name}
                className="w-full h-full object-cover"
                data-testid={`${testIdPrefix}-photo`}
                loading="lazy"
              />
            </div>
            <h1
              className={`text-2xl sm:text-3xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
              data-testid={`${testIdPrefix}-name`}
            >
              {c.name}
            </h1>
            <p className={`text-sm mt-1 ${roleColor}`}>{c.role}</p>
            <p className={`text-xs mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{c.location}</p>

            {c.orcid_url && (
              <a
                href={c.orcid_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testIdPrefix}-orcid-link`}
                className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${isDark ? "border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/10" : "border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"}`}
              >
                <Globe2 className="w-3 h-3" />
                {c.orcid_label || "ORCID"}: {c.orcid}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {/* iter645 — Book-appointment CTA (SuperDoc) */}
            {c.booking_url && (
              <a
                href={c.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testIdPrefix}-booking-link`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow"
                style={{ background: "var(--brand-gradient-primary)" }}
              >
                <CalendarCheck className="w-4 h-4" />
                Запиши час
                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
              </a>
            )}

            <div className={`mt-5 p-3 rounded-xl text-xs leading-relaxed ${roleInAppBg}`}>
              <div className="flex items-center gap-1.5 mb-1 font-bold uppercase tracking-wider text-[10px]">
                <RoleIcon className="w-3 h-3" />
                {c.role_in_app_title}
              </div>
              {c.role_in_app}
            </div>
          </div>
        </aside>

        {/* RIGHT: bio + edu + quals */}
        <div className="lg:col-span-8 space-y-6">
          <div data-testid={`${testIdPrefix}-bio`}>
            {(c.bio || []).filter(Boolean).map((p, i) => (
              <p
                key={i}
                className={`text-base leading-relaxed mb-3 last:mb-0 ${isDark ? "text-slate-300" : "text-slate-700"}`}
              >
                {p}
              </p>
            ))}
          </div>

          <div>
            <h3 className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              <GraduationCap className="w-4 h-4" style={iconColor} />
              {c.edu_title}
            </h3>
            <ChipList items={c.edu} isDark={isDark} color={accent} />
          </div>

          <div>
            <h3 className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {isFuchsia ? (
                <Award className="w-4 h-4" style={iconColor} />
              ) : (
                <BookOpen className="w-4 h-4" style={iconColor} />
              )}
              {c.quals_title}
            </h3>
            <ChipList items={c.quals} isDark={isDark} color={accent} />
          </div>
        </div>
      </div>
    </section>
  );
}
